import { ButtonColor } from '@design-system/components/button/button.css';
import { getDurationDisplay } from '../../../../common/videos/helpers';
import { Folder, Folders, Video, Videos } from '../../models/videos.domain';
import {
  AddFromLibaryVM,
  AddFromLibraryVMDispatcher,
  VideoListItem,
  SelectTab,
} from '../../view-models/addFromLibrary.viewModel';
import { getFoldersChildrenCount } from '@client/new/common/library/helpers';
import { FolderListItem } from './libraryList';

export const getUploadText = (step: AddFromLibaryVM['upload']['step']) => {
  switch (step) {
    case 'Thumbnail':
      return 'Use slider to select the frame for the thumbnail';
    case 'Uploading':
      return 'Uploading in progress... Please do not close the tab!';
    default:
      return null;
  }
};

export type ButtonVariant = 'text' | 'filled' | 'outlined';

export const getFooterButtons = ({
  activeTab,
  dispatch,
  selectedIds,
  actionsDisabled,
  uploadStep,
  inProgress,
  disabled,
  closeModal,
}: {
  activeTab: SelectTab;
  dispatch: AddFromLibraryVMDispatcher;
  actionsDisabled: boolean;
  selectedIds: AddFromLibaryVM['library']['selectedIds'];
  uploadStep: AddFromLibaryVM['upload']['step'];
  inProgress: boolean;
  disabled?: boolean;
  closeModal: () => void;
}) => {
  const selectDisabled = !selectedIds;

  switch (activeTab) {
    case 'Library':
      return [
        {
          props: {
            color: 'neutral' as ButtonColor,
            variant: 'text' as ButtonVariant,
            onClick: () => closeModal(),
            disabled: false,
          },
          label: 'Cancel',
        },
        {
          props: {
            color: 'neutral' as ButtonColor,
            variant: 'filled' as ButtonVariant,
            onClick: () => dispatch('addVideo'),
            disabled:
              actionsDisabled || selectDisabled || selectedIds.length === 0,
          },
          label: 'Add',
        },
      ];
    case 'Upload': {
      if (uploadStep === 'Name') {
        return [
          {
            props: {
              color: 'neutral' as ButtonColor,
              variant: 'text' as ButtonVariant,
              onClick: () => dispatch('close'),
              disabled: false,
            },
            label: 'Cancel',
          },
          {
            props: {
              color: 'neutral' as ButtonColor,
              variant: 'filled' as ButtonVariant,
              onClick: () => dispatch('showSelectThumbnail'),
              disabled,
            },
            label: 'Next',
          },
        ];
      }
      return [
        {
          props: {
            color: 'neutral' as ButtonColor,
            variant: 'text' as ButtonVariant,
            onClick: () => dispatch('showEditName'),
            disabled: inProgress,
          },
          label: 'Back',
        },
        {
          props: {
            color: 'neutral' as ButtonColor,
            variant: 'filled' as ButtonVariant,
            onClick: () => dispatch('upload'),
            disabled: inProgress || disabled,
          },
          label: 'Upload',
        },
      ];
    }
  }
};

export const toListItem = (v: Video, onClick: () => void): VideoListItem => {
  const d = new Date(v.createdAt);

  return {
    id: v.id,
    imageUrl: v.thumbnailUrl,
    title: v.name,
    duration: getDurationDisplay(v.duration),
    tag: {
      label: v.type === 'short' ? '⚡️ Clip' : '📹 Video',
    },
    date: d.toLocaleDateString('en-US'),
    onClick,
  };
};

export const toFolderItem = ({
  item,
  onClick,
  folders,
  videos,
}: {
  item: Folder;
  onClick: () => void;
  folders: Folders;
  videos: Videos;
}): FolderListItem => ({
  id: item.id,
  name: item.name,
  count: getFoldersChildrenCount(folders, videos, item.id),
  parentId: item.parentId || null,
  updatedAt: item.updatedAt,
  onClick,
});
